import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { submitLeadWithGeoLocation, getCountryInfo } from '../services/centralLeadApi';
import { toast } from 'sonner';
import countryCodes from '../data/countryCodes';
import CountryCodeSelect from '../components/CountryCodeSelect';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [countryCode, setCountryCode] = useState('+1');
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [errors, setErrors] = useState({ email: '', phone: '' });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchGeo = async () => {
      try {
        const info = await getCountryInfo();
        if (info) {
          setDetectedCountry(info);
          const match = countryCodes.find(c => c.country === info.countryCode);
          if (match) setCountryCode(match.code);
        }
      } catch (e) { console.error('Geo fetch error:', e); }
    };
    fetchGeo();
  }, []);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => phone.replace(/[^\d]/g, '').length >= 10;

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };
    if (!validateEmail(formData.email)) { newErrors.email = 'Please provide a valid email address'; hasErrors = true; }
    if (!validatePhone(formData.phone)) { newErrors.phone = 'Please provide a valid 10-digit phone number'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); toast.error('Please correct the highlighted fields'); return; }

    setIsLoading(true);
    try {
      const leadData = { name: formData.name, email: formData.email, phone: formData.phone, countryCode, country: countryCodes.find(c => c.code === countryCode)?.country || 'US', description: formData.message };
      const result = await submitLeadWithGeoLocation(leadData, null, 'Contact Us Page');
      if (result.success) { toast.success(result.message || 'Message sent successfully.'); setFormData({ name: '', email: '', phone: '', message: '' }); }
      else { toast.error(result.message || 'Submission failed.'); setIsLoading(false); }
    } catch (error) { toast.error('An error occurred. Please try again.'); setIsLoading(false); }
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero */}
      <section className="relative bg-[#1A3628] py-20 overflow-hidden" data-testid="contact-hero">
       <div className="absolute inset-0  bg-center" style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1695388474402-ed805a890d8d?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85")' }}><div className="absolute inset-0 bg-gradient-to-br from-[#1A3628]/90 via-[#11241A]/90 to-[#0D1910]/95"></div></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center relative z-10">
          <p className="eyebrow text-[#C85A40] mb-4">Get in Touch</p>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-white mb-6 tracking-tighter">Contact Us</h1>
          <p className="font-body text-base text-white/70 max-w-2xl mx-auto">
            Have questions about protecting your intellectual property? Our attorneys are available to discuss your specific needs.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-24 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col-reverse lg:grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-0">
              {[
                { icon: Phone, title: 'Phone', line1: '+1 (213) 646 7133', line2: 'Mon-Fri, 9AM-6PM EST' },
                { icon: Mail, title: 'Email', line1: 'info@trademarkapplicationhub.com', line2: 'We respond within 24 hours' },
                { icon: MapPin, title: 'Office', line1: '7080 Hollywood Blvd Suite 700', line2: 'Los Angeles, CA 90028' },
                { icon: Clock, title: 'Business Hours', line1: 'Monday - Friday: 9AM - 6PM', line2: 'Saturday: 10AM - 4PM' }
              ].map((item, i) => (
                <div key={i} className="p-6 border border-[#D5D1C8] hover-gold-border" data-testid={`contact-info-${i}`}>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-[#1A3628] flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-[#C85A40]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-display text-sm font-bold text-[#111C16] mb-1">{item.title}</h3>
                      <p className="font-body text-sm text-[#4A5D53]">{item.line1}</p>
                      <p className="font-body text-xs text-[#4A5D53/60] mt-0.5">{item.line2}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <div className="p-8 border border-[#D5D1C8]">
                <p className="eyebrow text-[#C85A40] mb-2">Send Us a Message</p>
                <h2 className="font-display text-2xl font-bold text-[#111C16] mb-6">How Can We Help?</h2>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input type="text" placeholder="Full Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="rounded-3xl border-[#D5D1C8] font-body" data-testid="contact-form-name" />
                    <div>
                      <Input type="email" placeholder="Email Address" value={formData.email} onChange={(e) => { setFormData({...formData, email: e.target.value}); if (errors.email && validateEmail(e.target.value)) setErrors(prev => ({...prev, email: ''})); }} onBlur={() => { if (formData.email && !validateEmail(formData.email)) setErrors(prev => ({...prev, email: 'Please provide a valid email'})); else setErrors(prev => ({...prev, email: ''})); }} className={`rounded-3xl font-body ${errors.email ? 'border-red-500' : 'border-[#D5D1C8]'}`} required data-testid="contact-form-email" />
                      {errors.email && <p className="text-red-500 text-xs mt-1 font-body">{errors.email}</p>}
                    </div>
                  </div>
                  <div>
                    <div className="flex gap-2">
                      <CountryCodeSelect value={countryCode} onChange={setCountryCode} countryCodes={countryCodes} className="w-28" testId="contact-form-country" />
                      <Input type="tel" placeholder="Phone Number" value={formData.phone} onChange={(e) => { const v = e.target.value.replace(/[^\d\s\-]/g, ''); setFormData({...formData, phone: v}); }} className={`flex-1 rounded-3xl font-body ${errors.phone ? 'border-red-500' : 'border-[#D5D1C8]'}`} required data-testid="contact-form-phone" />
                    </div>
                    {/* {detectedCountry && <p className="font-body text-xs text-[#4A5D53/60] mt-1">Detected: {detectedCountry.countryName}</p>} */}
                    {errors.phone && <p className="text-red-500 text-xs mt-1 font-body">{errors.phone}</p>}
                  </div>
                  <Textarea placeholder="What would you like to protect? (Name, Logo, Slogan)" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required rows={5} className="rounded-3xl border-[#D5D1C8] font-body" data-testid="contact-form-message" />
                  <Button type="submit" disabled={isLoading} className="w-full bg-[#C85A40] hover:bg-[#A64730] text-[#111C16] btn-hover" data-testid="contact-form-submit">
                    {isLoading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
